import { createTheme, alpha } from '@mui/material/styles';
import { DEFAULT_FONT_STACK } from '../constants/fonts';
import styleVariables from '../scss/_variables.scss';
import {
  THEME_SPACING_UNIT,
  DEFAULT_COMPONENT_MARGIN,
  DEFAULT_COMPONENT_PADDING,
} from '../constants/spacing';
import { FULL_WIDTH } from '../constants/Layout';
import { getActionReviewPalette } from './actionReviewTheme';

const themeDark = createTheme({
  spacing: THEME_SPACING_UNIT,
  typography: {
    fontFamily: DEFAULT_FONT_STACK,
  },
  palette: {
    actionReview: getActionReviewPalette('dark'),
    mode: 'dark',
    primary: {
      main: styleVariables.hanabiThemeColorDarkRed,
      contrastText: styleVariables.creamyWhite,
    },
    secondary: {
      main: styleVariables.motionButton,
      contrastText: styleVariables.creamyWhite,
    },
    error: {
      main: styleVariables.motionButton,
      dark: styleVariables.motionButton,
      contrastText: styleVariables.creamyWhite,
    },
    warning: {
      main: styleVariables.colorYellow,
      dark: styleVariables.colorYellow,
      contrastText: styleVariables.charcoalGrey,
    },
    info: {
      main: styleVariables.instructionsBlue,
      contrastText: styleVariables.creamyWhite,
    },
    background: {
      default: styleVariables.sealBrown,
      paper: styleVariables.chineseBlack,
    },
    text: {
      primary: styleVariables.colorPlatinum,
      secondary: styleVariables.creamyWhite,
      disabled: styleVariables.grayCyan,
    },
    success: {
      main: styleVariables.darkGreen,
      dark: styleVariables.darkGreen,
      contrastText: styleVariables.creamyWhite,
    },
    divider: styleVariables.philipineGray,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1.5),
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          padding: DEFAULT_COMPONENT_PADDING,
          backgroundColor: styleVariables.darkGray,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          width: FULL_WIDTH,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            paddingTop: '12px',
            paddingBottom: '12px',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginBottom: DEFAULT_COMPONENT_MARGIN,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '12px',
        },
      },
    },
    MuiListItem: {
      defaultProps: {
        dense: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: styleVariables.darkGray,
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            backgroundColor: styleVariables.darkGray,
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: styleVariables.darkGray,
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            backgroundColor: styleVariables.darkGray,
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: ({ theme }) => ({
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
          '& .MuiAlert-icon': {
            color: theme.palette.info.contrastText,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          // Custom styling for error buttons
          if (ownerState.color === 'error') {
            return {
              color: theme.palette.error.contrastText,
              border: `1px solid ${theme.palette.error.main}`,
              '&:hover': {
                backgroundColor: alpha(
                  theme.palette.error.main,
                  theme.palette.action.hoverOpacity,
                ),
                border: `1px solid ${theme.palette.error.main}`,
              },
            };
          }

          // Variant-based customizations
          const variants = [
            {
              props: { variant: 'outlined' },
              style: {
                color: styleVariables.creamyWhite,
              },
            },
          ];
          // Return the correct style by matching variant
          const variantStyle = variants.find(
            (v) => ownerState.variant === v.props.variant,
          );

          return variantStyle ? variantStyle.style : {};
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          if (ownerState.color === 'error') {
            return {
              color: theme.palette.error.contrastText,

              border: `1px solid ${theme.palette.error.main}`,

              '&:hover': {
                backgroundColor: alpha(
                  theme.palette.error.main,
                  theme.palette.action.hoverOpacity,
                ),
                border: `1px solid ${theme.palette.error.main}`,
              },
            };
          }

          return {};
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: styleVariables.hanabiThemeColorBrightOrangeRed,
          textDecoration: 'underline',
        },
      },
    },
  },
});

export default themeDark;
